import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS,
  SyncWorkflowRemoveFileResponse,
} from '@workflows/types';
import { WorkflowStateService } from './workflow-state.service';
import {
  PhotoQueueBlobRecord,
  PhotoQueueTask,
} from './input-photos-queue.types';
import {
  PhotoQueueExecutionResult,
  PhotoQueueExecutor,
} from './photo-queue.types';
import {
  PHOTO_QUEUE_STALE_TARGET_CODE,
  PhotoQueueTaskExecutionError,
} from './photo-queue-error';

/** Сервер не нашёл регламент, форму или поле задачи: её цель устарела */
const STALE_TARGET_FAILURE_REASONS: readonly string[] = [
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.requestWorkflowNotFound,
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.formNotFound,
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.formHasNoField,
];

function isStaleTargetFailure(failureReason: string): boolean {
  return STALE_TARGET_FAILURE_REASONS.includes(failureReason);
}

function createStaleTargetError(
  failureReason: string,
): PhotoQueueTaskExecutionError {
  return new PhotoQueueTaskExecutionError(
    PHOTO_QUEUE_STALE_TARGET_CODE,
    `Stale target: ${failureReason}`,
    false,
  );
}

@Injectable({ providedIn: 'root' })
export class PhotoQueueExecutorService implements PhotoQueueExecutor {
  // WorkflowStateService, а не фасад: фасад зависит от очереди фото (сводка работы заявки)
  private readonly workflowService = inject(WorkflowStateService);

  async execute(
    task: PhotoQueueTask,
    blobRecord: PhotoQueueBlobRecord | null,
  ): Promise<PhotoQueueExecutionResult> {
    if (task.operation === 'upload') {
      return this.executeUpload(task, blobRecord);
    }

    return this.executeRemove(task);
  }

  private async executeUpload(
    task: Extract<PhotoQueueTask, { operation: 'upload' }>,
    blobRecord: PhotoQueueBlobRecord | null,
  ): Promise<PhotoQueueExecutionResult> {
    if (!blobRecord) {
      throw new PhotoQueueTaskExecutionError(
        'blob_missing',
        'Blob record not found for upload task',
        false,
      );
    }

    const file = new File([blobRecord.blob], blobRecord.fileName, {
      type: blobRecord.mimeType,
      lastModified: blobRecord.lastModified,
    });
    const formData = new FormData();

    formData.append('file', file);

    const response = await firstValueFrom(
      this.workflowService.syncWorkflowUploadFile(
        task.target.requestNumber,
        task.target.workflowId,
        task.target.nodeId,
        task.target.formId,
        task.target.fieldId,
        formData,
        task.id,
      ),
    );

    if (!response.isOk) {
      if (isStaleTargetFailure(response.failureReason)) {
        throw createStaleTargetError(response.failureReason);
      }

      throw new PhotoQueueTaskExecutionError(
        `upload_${response.failureReason}`,
        `Upload failed: ${response.failureReason}`,
        false,
      );
    }

    return {
      operation: 'upload',
      result: {
        createdSubmissionFileId: response.data.createdSubmissionFileId,
        uploadedToSubmissionId: response.data.uploadedToSubmissionId,
        uploadedFile: response.data.uploadedFile,
      },
    };
  }

  private async executeRemove(
    task: Extract<PhotoQueueTask, { operation: 'remove' }>,
  ): Promise<PhotoQueueExecutionResult> {
    const response = await firstValueFrom(
      this.workflowService.syncWorkflowRemoveFile(
        task.target.requestNumber,
        task.target.workflowId,
        task.target.nodeId,
        task.target.formId,
        task.target.fieldId,
        task.createdSubmissionFileId,
        task.id,
      ),
    );

    if (!response.isOk && !this.isSubmissionFileNotFoundResponse(response)) {
      if (isStaleTargetFailure(response.failureReason)) {
        throw createStaleTargetError(response.failureReason);
      }

      throw new PhotoQueueTaskExecutionError(
        `remove_${response.failureReason}`,
        `Remove failed: ${response.failureReason}`,
        false,
      );
    }

    return { operation: 'remove' };
  }

  private isSubmissionFileNotFoundResponse(
    response: SyncWorkflowRemoveFileResponse,
  ): boolean {
    return (
      !response.isOk &&
      response.failureReason ===
        SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.submissionFileNotFound
    );
  }
}
